import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Row, Col, Spinner } from 'react-bootstrap';
import { useMutation } from '@apollo/client';
import { CREATE_USER, UPDATE_USER } from '../../graphql/operations';

export interface CustomerFormState {
  username: string;
  password?: string;
  email?: string;
  phone?: string;
  companyName?: string;
  gstNumber?: string;
  companyAddress?: string;
  shippingAddress?: string;
}

interface CustomerModalProps {
  show: boolean;
  onHide: () => void;
  isEditing?: boolean;
  selectedCustomer?: any; // The full customer object if editing
  onCustomerSaved?: (savedCustomer: any) => void;
}

const CustomerModal: React.FC<CustomerModalProps> = ({
  show,
  onHide,
  isEditing = false,
  selectedCustomer = null,
  onCustomerSaved,
}) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [gstNumber, setGstNumber] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');

  const [formError, setFormError] = useState('');
  const [confirmSave, setConfirmSave] = useState(false);

  const [createUser, { loading: creating }] = useMutation(CREATE_USER);
  const [updateUser, { loading: updating }] = useMutation(UPDATE_USER);

  const loading = creating || updating;

  useEffect(() => {
    if (show) {
      setFormError('');
      setConfirmSave(false);
      if (isEditing && selectedCustomer) {
        setUsername(selectedCustomer.username || '');
        setPassword('');
        setEmail(selectedCustomer.email || '');
        setPhone(selectedCustomer.phone || '');
        setCompanyName(selectedCustomer.companyName || '');
        setGstNumber(selectedCustomer.gstNumber || '');
        setCompanyAddress(selectedCustomer.companyAddress || '');
        setShippingAddress(selectedCustomer.shippingAddress || '');
      } else {
        setUsername('');
        setPassword('');
        setEmail('');
        setPhone('');
        setCompanyName('');
        setGstNumber('');
        setCompanyAddress('');
        setShippingAddress('');
      }
    }
  }, [show, isEditing, selectedCustomer]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!username.trim()) {
      setFormError('Username is required.');
      return;
    }

    if (!isEditing && !password) {
      setFormError('Password is required for new accounts.');
      return;
    }

    if (phone.trim() && !/^\d{10}$/.test(phone.replace(/[\s\-+]/g, '').slice(-10))) {
      setFormError('Please enter a valid 10-digit phone number.');
      return;
    }

    if (email.trim() && !/\S+@\S+\.\S+/.test(email)) {
      setFormError('Please enter a valid email address.');
      return;
    }

    // Confirmation check on save if editing/saving
    if (isEditing && !confirmSave) {
      setConfirmSave(true);
      return;
    }

    try {
      if (isEditing && selectedCustomer) {
        const { data } = await updateUser({
          variables: {
            id: selectedCustomer.id,
            username: username.trim(),
            password: password.trim() || undefined,
            role: 'customer',
            email: email.trim(),
            phone: phone.trim(),
            companyName: companyName.trim(),
            gstNumber: gstNumber.trim().toUpperCase(),
            companyAddress: companyAddress.trim(),
            shippingAddress: shippingAddress.trim(),
          },
        });

        if (data?.updateUser?.success) {
          if (onCustomerSaved) onCustomerSaved(data.updateUser.user);
          onHide();
        } else {
          setFormError(data?.updateUser?.message || 'Failed to update customer profile.');
          setConfirmSave(false);
        }
      } else {
        const { data } = await createUser({
          variables: {
            username: username.trim(),
            password: password.trim(),
            role: 'customer',
            email: email.trim(),
            phone: phone.trim(),
            companyName: companyName.trim(),
            gstNumber: gstNumber.trim().toUpperCase(),
            companyAddress: companyAddress.trim(),
            shippingAddress: shippingAddress.trim(),
          },
        });

        if (data?.createUser?.success) {
          if (onCustomerSaved) onCustomerSaved(data.createUser.user);
          onHide();
        } else {
          setFormError(data?.createUser?.message || 'Failed to create customer profile.');
        }
      }
    } catch (err: any) {
      setFormError(err.message || 'An error occurred. Please try again.');
      setConfirmSave(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" className="admin-customer-modal" style={{ zIndex: 1060 }}>
      <Modal.Header closeButton className="border-bottom-0 pb-0">
        <Modal.Title className="fw-bold text-dark">
          {isEditing ? 'Edit Customer Profile' : 'Add New Customer Profile'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body className="p-4">
          {formError && (
            <Alert variant="danger" className="border-0 py-2 rounded-3 mb-3 d-flex align-items-center gap-2" style={{ fontSize: '12.5px' }}>
              <i className="fas fa-exclamation-triangle"></i>
              <div>{formError}</div>
            </Alert>
          )}

          <Row className="g-3">
            <Col md={6}>
              <Form.Group controlId="custUsername">
                <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '13px' }}>Username *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter login username"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    setConfirmSave(false);
                  }}
                  style={{ borderRadius: '8px', fontSize: '13.5px' }}
                  required
                  disabled={loading}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="custPassword">
                <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '13px' }}>
                  Password {isEditing && <span className="text-muted fw-normal" style={{ fontSize: '11px' }}>(Leave blank to keep current)</span>}
                </Form.Label>
                <Form.Control
                  type="password"
                  placeholder={isEditing ? '••••••••' : 'Enter password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setConfirmSave(false);
                  }}
                  style={{ borderRadius: '8px', fontSize: '13.5px' }}
                  required={!isEditing}
                  disabled={loading}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="custPhone">
                <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '13px' }}>Phone Number</Form.Label>
                <Form.Control
                  type="tel"
                  placeholder="e.g. 9876543210"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    setConfirmSave(false);
                  }}
                  style={{ borderRadius: '8px', fontSize: '13.5px' }}
                  disabled={loading}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="custEmail">
                <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '13px' }}>Email Address</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="e.g. customer@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setConfirmSave(false);
                  }}
                  style={{ borderRadius: '8px', fontSize: '13.5px' }}
                  disabled={loading}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="custCompanyName">
                <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '13px' }}>Company Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. Acme Corporation"
                  value={companyName}
                  onChange={(e) => {
                    setCompanyName(e.target.value);
                    setConfirmSave(false);
                  }}
                  style={{ borderRadius: '8px', fontSize: '13.5px' }}
                  disabled={loading}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="custGstNumber">
                <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '13px' }}>GSTIN Number</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. 33AAAAA1111A1Z1"
                  value={gstNumber}
                  onChange={(e) => {
                    setGstNumber(e.target.value);
                    setConfirmSave(false);
                  }}
                  style={{ borderRadius: '8px', fontSize: '13.5px' }}
                  disabled={loading}
                />
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Group controlId="custCompanyAddress">
                <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '13px' }}>Company / Billing Address</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Billing address..."
                  value={companyAddress}
                  onChange={(e) => {
                    setCompanyAddress(e.target.value);
                    setConfirmSave(false);
                  }}
                  style={{ borderRadius: '8px', fontSize: '13.5px' }}
                  disabled={loading}
                />
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Group controlId="custShippingAddress">
                <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '13px' }}>Shipping / Delivery Address</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Shipping address..."
                  value={shippingAddress}
                  onChange={(e) => {
                    setShippingAddress(e.target.value);
                    setConfirmSave(false);
                  }}
                  style={{ borderRadius: '8px', fontSize: '13.5px' }}
                  disabled={loading}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="border-top-0 pt-0 px-4 pb-4">
          <Button variant="outline-secondary" onClick={onHide} style={{ borderRadius: '8px', fontSize: '13px' }} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant={confirmSave ? 'danger' : 'primary'}
            style={{ borderRadius: '8px', fontSize: '13px' }}
            disabled={loading}
          >
            {loading ? (
              <Spinner size="sm" animation="border" className="me-1" />
            ) : confirmSave ? (
              <><i className="fas fa-exclamation-triangle me-1" />Confirm Save</>
            ) : (
              'Save Customer'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CustomerModal;
